import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import capitalize from "lodash/capitalize";

import { CardGroup, MetricCard } from "@similarweb/pro-logic-components";

import { translate } from "app/filters/i18nFilter";
import type { ICompetitiveOpportunitiesData } from "app/pages/keyword-research/website-analysis/pages/seo-overview/types";
import { EPresetOption } from "app/common/keyword-research/website-keywords/competitiveFilterPresets";
import { transformSiteListToParam } from "pro-features/components/AutocompleteWebsites/utils";
import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";

import { valueFormatter } from "../utils";

const CONTAINER_WIDTH = "307px";
const CONTAINER_HEIGHT = "460px";

interface IPageParams {
    keys: string[];
    pageFilter: IChosenItemParams;
    sourceType: string;
    country: number;
}

interface IWebsiteKeywordsPresetsProps {
    isLoading?: boolean;
    data?: ICompetitiveOpportunitiesData;
    pageParams: IPageParams;
}
enum MetricType {
    Opportunities = "Opportunities",
    Losses = "Losses",
    Wins = "Wins",
    HighlyCompetitive = "HighlyCompetitive",
    Overlapping = "Overlapping",
}
interface ICardParams extends IPageParams {
    duration: string;
}
const defaultData: ICompetitiveOpportunitiesData = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Total: 0,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    TotalPresets: {
        [EPresetOption.Opportunities]: 0,
        [EPresetOption.Losses]: 0,
        [EPresetOption.Wins]: 0,
        [EPresetOption.HighlyCompetitive]: 0,
        [EPresetOption.Overlapping]: 0,
        [EPresetOption.Cannibalization]: 0,
        [EPresetOption.LongTail]: 0,
        [EPresetOption.SerpFeatures]: 0,
        [EPresetOption.HighTraffic]: 0,
        [EPresetOption.LowPotential]: 0,
    },
};
function metricCardClicked(item: MetricType, cardParams: ICardParams): () => void {
    const { keys, sourceType, ...restCardParams } = cardParams;
    const proNavigatorParams = {
        ...restCardParams,
        key: keys.join(","),
        pageFilter: transformSiteListToParam(cardParams.pageFilter),
        trafficPreset: item,
        selectedPageTab: capitalize(sourceType),
    };
    return (): void => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", `Competitive Opportunitie/Opportunitie/${item}`);
        void proNavigator.go("organicsearch_websites_v2_traffic_keywords", proNavigatorParams);
    };
}
export const WebsiteKeywordsPresets: FC<IWebsiteKeywordsPresetsProps> = ({
    isLoading = true,
    data = defaultData,
    pageParams,
}) => {
    const { params } = useCurrentStateAndParams();
    const cardParams: ICardParams = {
        ...pageParams,
        duration: params.duration as string,
    };
    const ctaIconName = "arrow-slant-right";
    const metricCardTitlePrefix = "website.keywords.2.0.page.table.overview.competitive.filter";

    const valueDescriptor = translate("common.keywords");
    return (
        <CardGroup
            $maxWidth={CONTAINER_WIDTH}
            $height={CONTAINER_HEIGHT}
            withDividers={true}
            groupDirection="column"
        >
            <MetricCard
                isLoading={isLoading}
                ctaIconName={ctaIconName}
                title={translate(`${metricCardTitlePrefix}.opportunities`)}
                tooltip={translate(`${metricCardTitlePrefix}.opportunities.tooltip`)}
                value={valueFormatter(data.TotalPresets.Opportunities)}
                valueDescriptor={valueDescriptor}
                onCardClick={metricCardClicked(MetricType.Opportunities, cardParams)}
            />
            <MetricCard
                isLoading={isLoading}
                ctaIconName={ctaIconName}
                title={translate(`${metricCardTitlePrefix}.losses`)}
                tooltip={translate(`${metricCardTitlePrefix}.losses.tooltip`)}
                value={valueFormatter(data.TotalPresets.Losses)}
                valueDescriptor={valueDescriptor}
                onCardClick={metricCardClicked(MetricType.Losses, cardParams)}
            />
            <MetricCard
                isLoading={isLoading}
                ctaIconName={ctaIconName}
                title={translate(`${metricCardTitlePrefix}.wins`)}
                tooltip={translate(`${metricCardTitlePrefix}.wins.tooltip`)}
                value={valueFormatter(data.TotalPresets.Wins)}
                valueDescriptor={valueDescriptor}
                onCardClick={metricCardClicked(MetricType.Wins, cardParams)}
            />
            <MetricCard
                isLoading={isLoading}
                ctaIconName={ctaIconName}
                title={translate(`${metricCardTitlePrefix}.highly_competitive`)}
                tooltip={translate(`${metricCardTitlePrefix}.highly_competitive.tooltip`)}
                value={valueFormatter(data.TotalPresets.HighlyCompetitive)}
                valueDescriptor={valueDescriptor}
                onCardClick={metricCardClicked(MetricType.HighlyCompetitive, cardParams)}
            />
            {/* <MetricCard
                isLoading={isLoading}
                ctaIconName={ctaIconName}
                title={translate(`${metricCardTitlePrefix}.competitor_winning`)}
                tooltip={translate(`${metricCardTitlePrefix}.competitor_winning.tooltip`)}
                value={valueFormatter(data.TotalPresets.Overlapping)}
                valueDescriptor={valueDescriptor}
                onCardClick={metricCardClicked(MetricType.Overlapping, cardParams)}
            /> */}
        </CardGroup>
    );
};
